/**
 * A community's courses as files (gh #5003, Workspace layout): `courses/<course>/course.yaml` —
 * the course, its modules, and each module's lessons in order — and `courses/<course>/lessons/
 * <lesson>.md`, each lesson's frontmatter and its Markdown text. `community.yaml` lists the course
 * folders in display order (`files.ts`).
 *
 * A course folder's and a lesson file's name is its STABLE LOCAL NAME, as a room's `key` is: chosen
 * once, kept through renames, the name a create's `client_id` derives from. A module's is its `key`.
 *
 * `course.yaml`'s `synced:` block is the base its orders are compared against: the ids of the
 * course's modules, and of each module's lessons, in the server's order as of the last sync —
 * kept for every module of the course, a module the files leave out included. Pull and push write
 * it; nothing else should.
 *
 * Workspace files are untrusted input (Decision 10): every name a file gives is a plain name, every
 * folder a real directory, and every read and write goes through the no-follow helpers of
 * `lib/fs-safety.ts`. Each file is checked against the `@wayai/core` bodies before any request;
 * problems are collected so one run names them all. A folder `community.yaml` does not list, and a
 * lesson file no module lists, is a draft in progress: warned about and skipped.
 *
 * Write-back is canonical: a fixed key order, comments not kept. A lesson's text is written with
 * exactly one trailing newline and read with exactly one stripped, so leading newlines, trailing
 * ones and a text that itself starts with `---` round-trip unchanged.
 */

import * as fs from 'node:fs';
import * as path from 'node:path';
import * as yaml from 'js-yaml';
import { communityCourseCreateBody, communityLessonCreateBody, communityModuleCreateBody } from '@wayai/core/contracts';
import { PATH_SAFE_ID_RULE, isPathSafeId } from '../lib/base-id.js';
import { COMMUNITY_FILE, readYamlIdNoFollow, uuidField } from '../lib/community-workspace.js';
import { readFileNoFollow, readRealFileOrThrow, requireRealSubdirNoSymlink, writeFileIfChangedNoFollow } from '../lib/fs-safety.js';
import { isRealDirectory } from '../lib/layout.js';
import { YAML_DUMP_OPTIONS } from '../lib/yaml-writer.js';
import { UUID_RE } from '../lib/utils.js';
import { toLf } from './model.js';

export const COURSES_DIR = 'courses';
export const COURSE_FILE = 'course.yaml';
export const LESSONS_DIR = 'lessons';
export const LESSON_FILE_EXT = '.md';

export interface LessonLink {
  title: string;
  url: string;
}

/** `lessons/<slug>.md`. No `id` yet: push creates it. */
export interface LessonFile {
  slug: string;
  id?: string;
  rev?: string;
  title: string;
  published: boolean;
  /** A room's `key` in `community.yaml`; none when absent. */
  room?: string;
  /** The URL as written (push sends it so); none when absent. */
  video_url?: string;
  links: LessonLink[];
  requires_paid_plan: boolean;
  /** The Markdown text, LF, less the one trailing newline the file ends with. */
  body: string;
}

/** A module in `course.yaml`; its `lessons` in list order. No `id` yet: push creates it. */
export interface ModuleEntry {
  key: string;
  id?: string;
  rev?: string;
  title: string;
  lessons: LessonFile[];
}

/**
 * A course's orders as of the last sync (`synced:`), by id: its modules, and each module's
 * lessons, by module id — every module of the course on the server then, whether the files list it
 * or not.
 */
export interface CourseSynced {
  modules?: string[];
  lessons: Record<string, string[]>;
}

/** A file or folder no list names: skipped. Its id, when it plainly holds one, so pull never writes it twice. */
export interface Unlisted {
  name: string;
  id: string | null;
}

/** `courses/<slug>/`. No `id` yet: push creates it, as a draft. */
export interface CourseFolder {
  slug: string;
  id?: string;
  rev?: string;
  title: string;
  /** '' for none. */
  description: string;
  published: boolean;
  modules: ModuleEntry[];
  synced?: CourseSynced;
  /** Lesson files in `lessons/` no module lists. */
  unlisted: Unlisted[];
}

const COURSE_KEYS = ['id', 'rev', 'title', 'description', 'published', 'modules', 'synced'];
const MODULE_KEYS = ['key', 'id', 'rev', 'title', 'lessons'];
const COURSE_SYNCED_KEYS = ['modules', 'lessons'];
const LESSON_KEYS = ['id', 'rev', 'title', 'published', 'room', 'video_url', 'links', 'requires_paid_plan'];

/** A lesson's fields a file holds, checked against the lesson create's own body. */
const lessonFileBody = communityLessonCreateBody.omit({ module_id: true, room_id: true, client_id: true });

// ── Checking a parsed YAML document (also `community.yaml`'s, in `files.ts`) ──

export type Doc = Record<string, unknown>;

export function isMapping(value: unknown): value is Doc {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function unknownKeys(doc: Doc, allowed: string[], where: string, problems: string[]): void {
  for (const key of Object.keys(doc)) {
    if (!allowed.includes(key)) problems.push(`${where}: unknown field ${JSON.stringify(key)}`);
  }
}

export function optionalString(doc: Doc, key: string, where: string, problems: string[]): string | undefined {
  const value = doc[key];
  if (value === undefined || value === null) return undefined;
  if (typeof value !== 'string') problems.push(`${where}: ${key} must be text`);
  return typeof value === 'string' ? value : undefined;
}

function optionalBoolean(doc: Doc, key: string, where: string, problems: string[]): boolean {
  const value = doc[key];
  if (value === undefined || value === null) return false;
  if (typeof value !== 'boolean') problems.push(`${where}: ${key} must be true or false`);
  return value === true;
}

/** An `id` (a UUID) and its `rev`; a rev with no id is refused, since it is the base of an entity that does not exist. */
function identity(doc: Doc, what: string, where: string, problems: string[]): { id?: string; rev?: string } {
  const id = optionalString(doc, 'id', where, problems);
  if (id !== undefined && !UUID_RE.test(id)) problems.push(`${where}: id must be ${what}'s id (a UUID)`);
  const rev = optionalString(doc, 'rev', where, problems);
  if (id === undefined && rev !== undefined) problems.push(`${where}: a rev without an id — remove the rev to create the ${what}`);
  return { ...(id === undefined ? {} : { id }), ...(rev === undefined ? {} : { rev }) };
}

/** The first message of a failed contract parse, as one line. */
export function contractProblem(error: { issues: Array<{ path: PropertyKey[]; message: string }> }): string {
  const [issue] = error.issues;
  return issue.path.length > 0 ? `${issue.path.map(String).join('.')}: ${issue.message}` : issue.message;
}

/** A list of ids (UUIDs), each once, or none when absent. */
export function readIdList(raw: unknown, where: string, problems: string[]): string[] | undefined {
  if (raw === undefined || raw === null) return undefined;
  if (!Array.isArray(raw) || raw.some((id) => typeof id !== 'string' || !UUID_RE.test(id)) || new Set(raw).size !== raw.length) {
    problems.push(`${where} must be a list of ids, each once — leave it as pull and push write it`);
    return undefined;
  }
  return raw as string[];
}

function readCourseSynced(raw: unknown, where: string, problems: string[]): CourseSynced | undefined {
  if (raw === undefined || raw === null) return undefined;
  if (!isMapping(raw)) {
    problems.push(`${where}: synced must be a mapping`);
    return undefined;
  }
  unknownKeys(raw, COURSE_SYNCED_KEYS, `${where} synced`, problems);
  const lessons: Record<string, string[]> = {};
  if (raw.lessons !== undefined && raw.lessons !== null) {
    if (!isMapping(raw.lessons)) problems.push(`${where}: synced.lessons must be a mapping of module ids`);
    else {
      for (const [moduleId, ids] of Object.entries(raw.lessons)) {
        if (!UUID_RE.test(moduleId)) problems.push(`${where}: synced.lessons: ${JSON.stringify(moduleId)} is not a module id`);
        const list = readIdList(ids, `${where} synced.lessons.${moduleId}`, problems);
        if (list) lessons[moduleId] = list;
      }
    }
  }
  const modules = readIdList(raw.modules, `${where} synced.modules`, problems);
  return { ...(modules === undefined ? {} : { modules }), lessons };
}

/** A list of plain names (course folders, lesson files), each once. */
export function readNameList(raw: unknown, where: string, what: string, problems: string[]): string[] {
  if (raw === undefined || raw === null) return [];
  if (!Array.isArray(raw)) {
    problems.push(`${where} must be a list of ${what} names`);
    return [];
  }
  const names: string[] = [];
  for (const name of raw) {
    if (typeof name !== 'string' || !isPathSafeId(name)) {
      problems.push(`${where}: ${JSON.stringify(name)} is not a ${what} name — ${PATH_SAFE_ID_RULE}`);
    } else if (names.includes(name)) {
      problems.push(`${where}: ${name} is listed twice`);
    } else {
      names.push(name);
    }
  }
  return names;
}

// ── Reading ──

/**
 * Read the course folders `slugs` names (in that order) under `communityFolder`, and note the
 * folders it does not. Problems go to `problems`; a symlink anywhere on the way throws.
 */
export function readCourseFolders(communityFolder: string, slugs: string[], problems: string[]): { courses: CourseFolder[]; unlisted: Unlisted[] } {
  const coursesDir = path.join(communityFolder, COURSES_DIR);
  requireRealSubdirNoSymlink(communityFolder, coursesDir, false);
  const courses: CourseFolder[] = [];
  for (const slug of slugs) {
    const folder = path.join(coursesDir, slug);
    requireRealSubdirNoSymlink(communityFolder, folder, false);
    if (!isRealDirectory(folder)) {
      problems.push(`${COMMUNITY_FILE} courses: ${slug} has no folder ${COURSES_DIR}/${slug}/`);
      continue;
    }
    const course = readCourseFolder(communityFolder, folder, slug, problems);
    if (course) courses.push(course);
  }
  const unlisted = folderEntries(coursesDir)
    .filter((name) => !slugs.includes(name) && isRealDirectory(path.join(coursesDir, name)))
    .map((name) => ({ name, id: readYamlIdNoFollow(communityFolder, path.join(coursesDir, name, COURSE_FILE), 'id') }));
  return { courses, unlisted };
}

function readCourseFolder(communityFolder: string, folder: string, slug: string, problems: string[]): CourseFolder | null {
  const where = `${COURSES_DIR}/${slug}/${COURSE_FILE}`;
  const text = readRealFileOrThrow(communityFolder, path.join(folder, COURSE_FILE));
  if (text === null) {
    problems.push(`${COURSES_DIR}/${slug}/: no ${COURSE_FILE}`);
    return null;
  }
  let doc: unknown;
  try {
    doc = yaml.load(text);
  } catch (err) {
    problems.push(`${where} is not valid YAML: ${err instanceof Error ? err.message : String(err)}`);
    return null;
  }
  if (!isMapping(doc)) {
    problems.push(`${where} must be a YAML mapping`);
    return null;
  }
  unknownKeys(doc, COURSE_KEYS, where, problems);
  const { id, rev } = identity(doc, 'a course', where, problems);
  const synced = readCourseSynced(doc.synced, where, problems);
  if (id === undefined && synced !== undefined) problems.push(`${where}: synced without an id — remove it to create the course`);
  const description = doc.description ?? '';
  const parsed = communityCourseCreateBody.safeParse({ title: doc.title, description });
  if (!parsed.success) problems.push(`${where}: ${contractProblem(parsed.error)}`);
  const published = optionalBoolean(doc, 'published', where, problems);

  const lessonsDir = path.join(folder, LESSONS_DIR);
  requireRealSubdirNoSymlink(communityFolder, lessonsDir, false);
  const modules = readModules(communityFolder, lessonsDir, doc.modules, slug, where, problems);
  const listed = new Set(modules.flatMap((module) => module.lessons.map((lesson) => lesson.slug)));
  const unlisted = folderEntries(lessonsDir)
    .filter((name) => name.endsWith(LESSON_FILE_EXT) && !listed.has(path.basename(name, LESSON_FILE_EXT)))
    .map((name) => ({ name, id: uuidField(lessonFrontmatter(readFileNoFollow(communityFolder, path.join(lessonsDir, name))), 'id') }));
  if (!parsed.success) return null;
  return {
    slug,
    ...(id === undefined ? {} : { id }),
    ...(rev === undefined ? {} : { rev }),
    title: doc.title as string,
    description: description as string,
    published,
    modules,
    ...(synced === undefined ? {} : { synced }),
    unlisted,
  };
}

/** The modules list: each key once and a plain name, each title a valid create, each lesson listed once in the course and backed by its file. */
function readModules(communityFolder: string, lessonsDir: string, raw: unknown, courseSlug: string, courseWhere: string, problems: string[]): ModuleEntry[] {
  if (raw === undefined || raw === null) return [];
  if (!Array.isArray(raw)) {
    problems.push(`${courseWhere}: modules must be a list`);
    return [];
  }
  const modules: ModuleEntry[] = [];
  const keys = new Set<string>();
  const listedLessons = new Set<string>();
  raw.forEach((entry, index) => {
    const at = `${courseWhere} modules[${index}]`;
    if (!isMapping(entry)) {
      problems.push(`${at}: must be a mapping`);
      return;
    }
    unknownKeys(entry, MODULE_KEYS, at, problems);
    const key = entry.key;
    if (typeof key !== 'string' || !isPathSafeId(key)) {
      problems.push(`${at}: key must be a plain name — ${PATH_SAFE_ID_RULE}`);
      return;
    }
    const where = `${courseWhere} module ${key}`;
    if (keys.has(key)) problems.push(`${where}: the key is used twice`);
    keys.add(key);
    const { id, rev } = identity(entry, 'a module', where, problems);
    const parsed = communityModuleCreateBody.safeParse({ title: entry.title });
    if (!parsed.success) problems.push(`${where}: ${contractProblem(parsed.error)}`);
    const lessons: LessonFile[] = [];
    for (const slug of readNameList(entry.lessons, `${where} lessons`, 'lesson', problems)) {
      if (listedLessons.has(slug)) {
        problems.push(`${courseWhere}: lesson ${slug} is listed twice`);
        continue;
      }
      listedLessons.add(slug);
      const lesson = readLessonFile(communityFolder, lessonsDir, courseSlug, slug, problems);
      if (lesson) lessons.push(lesson);
    }
    if (!parsed.success) return;
    modules.push({
      key,
      ...(id === undefined ? {} : { id }),
      ...(rev === undefined ? {} : { rev }),
      title: entry.title as string,
      lessons,
    });
  });
  return modules;
}

/** `text` split into its frontmatter and its body: `---` alone on the first line, YAML, `---` alone on a line, the text. */
function splitFrontmatter(text: string): { front: string; body: string } | null {
  if (!text.startsWith('---\n')) return null;
  const close = text.indexOf('\n---\n', 3);
  if (close === -1) return text.endsWith('\n---') ? { front: text.slice(4, -3), body: '' } : null;
  const rest = text.slice(close + 5);
  return { front: text.slice(4, close + 1), body: rest.endsWith('\n') ? rest.slice(0, -1) : rest };
}

function readLessonFile(communityFolder: string, lessonsDir: string, courseSlug: string, slug: string, problems: string[]): LessonFile | null {
  const name = `${slug}${LESSON_FILE_EXT}`;
  const where = `${COURSES_DIR}/${courseSlug}/${LESSONS_DIR}/${name}`;
  const raw = readRealFileOrThrow(communityFolder, path.join(lessonsDir, name));
  if (raw === null) {
    const hint = slug.endsWith(LESSON_FILE_EXT) ? ` (list a lesson by its file's name less ${LESSON_FILE_EXT})` : '';
    problems.push(`${COURSES_DIR}/${courseSlug}/${COURSE_FILE}: lesson ${slug} has no file ${LESSONS_DIR}/${name}${hint}`);
    return null;
  }
  const parts = splitFrontmatter(toLf(raw));
  if (!parts) {
    problems.push(`${where}: must start with a frontmatter block (a line --- , the lesson's fields, a line ---)`);
    return null;
  }
  let doc: unknown;
  try {
    doc = parts.front.trim() === '' ? {} : yaml.load(parts.front);
  } catch (err) {
    problems.push(`${where}: the frontmatter is not valid YAML: ${err instanceof Error ? err.message : String(err)}`);
    return null;
  }
  if (!isMapping(doc)) {
    problems.push(`${where}: the frontmatter must be a YAML mapping`);
    return null;
  }
  unknownKeys(doc, LESSON_KEYS, where, problems);
  const { id, rev } = identity(doc, 'a lesson', where, problems);
  const room = optionalString(doc, 'room', where, problems);
  if (room !== undefined && !isPathSafeId(room)) problems.push(`${where}: room must be a room's key in ${COMMUNITY_FILE}`);
  const videoUrl = optionalString(doc, 'video_url', where, problems);
  const published = optionalBoolean(doc, 'published', where, problems);
  const requiresPaidPlan = optionalBoolean(doc, 'requires_paid_plan', where, problems);
  const links = doc.links ?? [];
  const parsed = lessonFileBody.safeParse({
    title: doc.title,
    ...(videoUrl === undefined ? {} : { video_url: videoUrl }),
    body: parts.body,
    links,
  });
  if (!parsed.success) {
    problems.push(`${where}: ${contractProblem(parsed.error)}`);
    return null;
  }
  return {
    slug,
    ...(id === undefined ? {} : { id }),
    ...(rev === undefined ? {} : { rev }),
    title: doc.title as string,
    published,
    ...(room === undefined ? {} : { room }),
    ...(videoUrl === undefined ? {} : { video_url: videoUrl }),
    links: (links as LessonLink[]).map((link) => ({ title: link.title, url: link.url })),
    requires_paid_plan: requiresPaidPlan,
    body: parts.body,
  };
}

function folderEntries(dir: string): string[] {
  try {
    return fs.readdirSync(dir).filter((name) => !name.startsWith('.')).sort();
  } catch {
    return [];
  }
}

/** A lesson file's frontmatter, parsed; null for anything else. Best-effort: for an unlisted file's id. */
function lessonFrontmatter(bytes: Buffer | null): unknown {
  const parts = bytes ? splitFrontmatter(toLf(bytes.toString('utf-8'))) : null;
  if (!parts) return null;
  try {
    return yaml.load(parts.front);
  } catch {
    return null;
  }
}

// ── Writing ──

/** `course.yaml` as written: a fixed key order, revs and ids only where known. */
export function renderCourseYaml(course: CourseFolder): string {
  const doc = {
    ...(course.id ? { id: course.id } : {}),
    ...(course.rev ? { rev: course.rev } : {}),
    title: course.title,
    description: course.description,
    published: course.published,
    modules: course.modules.map((module) => ({
      key: module.key,
      ...(module.id ? { id: module.id } : {}),
      ...(module.rev ? { rev: module.rev } : {}),
      title: module.title,
      lessons: module.lessons.map((lesson) => lesson.slug),
    })),
    ...(course.synced
      ? {
        synced: {
          ...(course.synced.modules ? { modules: course.synced.modules } : {}),
          lessons: Object.fromEntries(
            Object.entries(course.synced.lessons).sort(([a], [b]) => syncedRank(course, a) - syncedRank(course, b) || a.localeCompare(b)),
          ),
        },
      }
      : {}),
  };
  return yaml.dump(doc, YAML_DUMP_OPTIONS);
}

/** Where a module's synced lessons are written: in the synced modules' order, the others after. */
function syncedRank(course: CourseFolder, moduleId: string): number {
  const at = course.synced?.modules?.indexOf(moduleId) ?? -1;
  return at === -1 ? Number.MAX_SAFE_INTEGER : at;
}

/** A lesson file as written: its frontmatter, then its text with one trailing newline. */
export function renderLessonFile(lesson: LessonFile): string {
  const front = {
    ...(lesson.id ? { id: lesson.id } : {}),
    ...(lesson.rev ? { rev: lesson.rev } : {}),
    title: lesson.title,
    published: lesson.published,
    ...(lesson.room ? { room: lesson.room } : {}),
    ...(lesson.video_url ? { video_url: lesson.video_url } : {}),
    ...(lesson.links.length > 0 ? { links: lesson.links.map((link) => ({ title: link.title, url: link.url })) } : {}),
    requires_paid_plan: lesson.requires_paid_plan,
  };
  return `---\n${yaml.dump(front, YAML_DUMP_OPTIONS)}---\n${toLf(lesson.body)}\n`;
}

export const coursePath = (course: CourseFolder) => path.join(COURSES_DIR, course.slug, COURSE_FILE);
export const lessonPath = (course: CourseFolder, lesson: LessonFile) => path.join(COURSES_DIR, course.slug, LESSONS_DIR, `${lesson.slug}${LESSON_FILE_EXT}`);

/** Write a course's `course.yaml` where it differs, no-follow (the chain from `root` real). Whether it wrote. */
export function writeCourseYaml(root: string, communityFolder: string, course: CourseFolder): boolean {
  return writeFileIfChangedNoFollow(root, path.join(communityFolder, coursePath(course)), renderCourseYaml(course));
}

/** Write a lesson's file where it differs, no-follow (the chain from `root` real). Whether it wrote. */
export function writeLessonFile(root: string, communityFolder: string, course: CourseFolder, lesson: LessonFile): boolean {
  return writeFileIfChangedNoFollow(root, path.join(communityFolder, lessonPath(course, lesson)), renderLessonFile(lesson));
}

/** Write a course's `course.yaml` and every lesson file its modules list, where they differ. The paths written. */
export function writeCourseFolder(root: string, communityFolder: string, course: CourseFolder): string[] {
  const written = writeCourseYaml(root, communityFolder, course) ? [coursePath(course)] : [];
  for (const module of course.modules) {
    for (const lesson of module.lessons) {
      if (writeLessonFile(root, communityFolder, course, lesson)) written.push(lessonPath(course, lesson));
    }
  }
  return written;
}

/** A file `courses/` holds that no list names, as a warning names it. */
export const unlistedPath = (name: string, course?: CourseFolder) =>
  course ? path.join(COURSES_DIR, course.slug, LESSONS_DIR, name) : `${path.join(COURSES_DIR, name)}/`;
